import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation, changeLanguage } from '../lib/i18n';
import { useTheme } from '../contexts/ThemeContext';
import apiClient from '../lib/apiClient';
import {
  Home,
  Users,
  User,
  Globe,
  Server,
  CreditCard,
  ChevronDown,
  Building,
  Map,
  Download,
  Settings,
  Menu,
  X,
  BarChart3,
  DollarSign,
  Calendar,
  Smartphone,
  Moon,
  Sun,
  LogOut,
  Search,
  Bell,
  Loader2,
  ExternalLink,
  MessageSquare
} from 'lucide-react';

// Available languages
const AVAILABLE_LANGUAGES = [
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' }
];

const TopNavigation = ({ user, onLogout }) => {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [appLogoUrl, setAppLogoUrl] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const userMenuRef = useRef(null);
  const languageMenuRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  // Load logo from database on mount
  useEffect(() => {
    const loadLogo = async () => {
      // First check localStorage cache
      const cachedLogo = localStorage.getItem('app_logo_url');
      if (cachedLogo && (cachedLogo.startsWith('http') || cachedLogo.startsWith('data:image/') || cachedLogo.startsWith('/'))) {
        setAppLogoUrl(cachedLogo);
      }

      try {
        // Fetch latest from API
        const response = await apiClient.get('/settings/logo');
        
        // API returns { success: true, data: "url..." } or { success: true, data: null }
        const logoData = response.data;
        
        if (logoData && typeof logoData === 'string' && (logoData.startsWith('http') || logoData.startsWith('data:image/') || logoData.startsWith('/'))) {
          // Update state if different
          if (logoData !== cachedLogo) {
            setAppLogoUrl(logoData);
          }
          // Update cache
          try {
            localStorage.setItem('app_logo_url', logoData);
          } catch (e) {
            // Silently ignore cache errors
          }
        } else if (!logoData) {
          // Clear if empty
          setAppLogoUrl(null);
          localStorage.removeItem('app_logo_url');
        }
      } catch (error) {
        // Use cache on error, don't log
      }
    };
    
    loadLogo();

    // Listen for logo updates from Settings page
    const handleLogoUpdate = (event) => {
      const newLogo = event.detail?.logo;
      if (newLogo && (newLogo.startsWith('http') || newLogo.startsWith('data:image/') || newLogo.startsWith('/'))) {
        setAppLogoUrl(newLogo);
        try {
          localStorage.setItem('app_logo_url', newLogo);
        } catch (e) {
          // Silently ignore cache errors
        }
      } else {
        setAppLogoUrl(null);
        localStorage.removeItem('app_logo_url');
      }
    };

    window.addEventListener('logoUpdated', handleLogoUpdate);

    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate);
    };
  }, []);

  // Smart Search Functionality
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    try {
      const results = await apiClient.post('/search', { query: query.trim() });
      setSearchResults(results.data || results);
      setShowSearchResults(true);
      setSelectedResultIndex(-1);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch(searchQuery);
      } else if (searchQuery.trim().length === 0) {
        setSearchResults(null);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation for search results
  const handleSearchKeyDown = (e) => {
    if (!showSearchResults || !searchResults?.results?.length) return;

    const totalResults = searchResults.results.reduce((sum, cat) => sum + cat.items.length, 0);
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedResultIndex(prev => (prev + 1) % totalResults);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedResultIndex(prev => prev <= 0 ? totalResults - 1 : prev - 1);
    } else if (e.key === 'Enter' && selectedResultIndex >= 0) {
      e.preventDefault();
      navigateToResult(selectedResultIndex);
    } else if (e.key === 'Escape') {
      setShowSearchResults(false);
      setSelectedResultIndex(-1);
    }
  };

  const navigateToResult = (index) => {
    let currentIndex = 0;
    for (const category of searchResults.results) {
      for (const item of category.items) {
        if (currentIndex === index) {
          setShowSearchResults(false);
          setSearchQuery('');
          setSearchResults(null);
          setSelectedResultIndex(-1);
          navigate(item.link);
          return;
        }
        currentIndex++;
      }
    }
  };

  const handleResultClick = (link) => {
    setShowSearchResults(false);
    setSearchQuery('');
    setSearchResults(null);
    setSelectedResultIndex(-1);
    navigate(link);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const toggleDropdown = (e, name) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const handleLanguageChange = async (langCode) => {
    try {
      await changeLanguage(langCode);
      setShowLanguageMenu(false);
    } catch (error) {
      console.error('Language change error:', error);
    }
  };

  const currentLanguage = AVAILABLE_LANGUAGES.find(lang => lang.code === i18n.language) || AVAILABLE_LANGUAGES[0];

  const handleLogout = () => {
    if (window.confirm(t('header.confirmLogout', 'Çıkış yapmak istediğinize emin misiniz?'))) {
      onLogout();
    }
  };

  const menuItems = [
    {
      name: 'dashboard',
      label: t('menu.dashboard', 'Dashboard'),
      icon: Home,
      path: '/dashboard',
      single: true
    },
    {
      name: 'orders',
      label: 'Siparişler',
      icon: CreditCard,
      items: [
        { path: '/company-payments', label: 'Ödemeler', icon: CreditCard },
        { path: '/domains', label: t('menu.domains', 'Domainler'), icon: Globe }
      ]
    },
    {
      name: 'analyses',
      label: t('menu.analyses', 'Analizler'),
      icon: BarChart3,
      items: [
        { path: '/panels', label: 'Ödeme Analizi', icon: BarChart3 }
      ]
    },
    {
      name: 'customer',
      label: 'Müşteri & Firma',
      icon: Users,
      items: [
        { path: '/customers', label: t('menu.customers', 'Müşteriler'), icon: Users },
        { path: '/companies', label: 'Şirketler', icon: Building }
      ]
    },
    {
      name: 'payments',
      label: 'Finansal İşlemler',
      icon: DollarSign,
      items: [
        { path: '/account-tracking', label: 'Cari Takip', icon: DollarSign },
        { path: '/calendar', label: 'Takvim', icon: Calendar }
      ]
    },
    {
      name: 'infrastructure',
      label: 'Altyapı',
      icon: Server,
      items: [
        { path: '/servers', label: t('menu.servers', 'Sunucular'), icon: Server },
        { path: '/registrars', label: 'Domain Firmaları', icon: Building }
      ]
    },
    {
      name: 'pricing',
      label: 'Fiyatlar',
      icon: CreditCard,
      items: [
        { path: '/domain-prices', label: t('menu.domainPrices', 'Domain Fiyatları'), icon: Globe },
        { path: '/hosting-prices', label: t('menu.hostingPrices', 'Hosting Fiyatları'), icon: Server }
      ]
    },
    {
      name: 'maps',
      label: 'Haritalar',
      icon: Map,
      path: '/maps',
      single: true
    },
    {
      name: 'support',
      label: 'Destek',
      icon: MessageSquare,
      path: '/support',
      single: true
    },
    {
      name: 'system',
      label: t('menu.system', 'Sistem'),
      icon: Settings,
      items: [
        { path: '/users', label: t('menu.users', 'Kullanıcılar'), icon: User },
        { path: '/data-management', label: t('menu.dataManagement', 'Veri Yönetimi'), icon: Download },
        { path: '/settings', label: t('menu.settings', 'Ayarlar'), icon: Settings },
        { path: '/mobile-access-keys', label: t('menu.mobileAccessKeys', 'Mobil Erişim'), icon: Smartphone }
      ]
    }
  ];

  const renderSingleItem = (item) => {
    const isItemActive = isActive(item.path);
    return (
      <Link
        key={item.name}
        to={item.path}
        className={`flex items-center space-x-1.5 px-3 py-1.5 rounded transition-all duration-200 text-xs font-medium ${
          isItemActive
            ? 'bg-blue-600 text-white'
            : 'text-gray-300 hover:bg-gray-700'
        }`}
      >
        <item.icon className="h-3.5 w-3.5" />
        <span className="whitespace-nowrap">{item.label}</span>
      </Link>
    );
  };

  const renderDropdown = (item) => {
    const isOpen = openDropdown === item.name;
    const hasActiveItem = item.items?.some(subItem => isActive(subItem.path));

    return (
      <div className="relative" key={item.name}>
        <button
          type="button"
          onClick={(e) => toggleDropdown(e, item.name)}
          className={`flex items-center space-x-1.5 px-3 py-1.5 rounded transition-all duration-200 text-xs font-medium ${
            hasActiveItem || isOpen
              ? 'bg-blue-600 text-white'
              : 'text-gray-300 hover:bg-gray-700'
          }`}
        >
          <item.icon className="h-3.5 w-3.5" />
          <span className="whitespace-nowrap">{item.label}</span>
          <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-48 bg-gray-800 border border-gray-700 rounded shadow-lg z-[99999] overflow-visible">
            <div className="py-1">
              {item.items.map((subItem) => {
                const Icon = subItem.icon;
                const isSubActive = isActive(subItem.path);

                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    onClick={() => setOpenDropdown(null)}
                    className={`flex items-center space-x-2.5 px-3 py-2 text-xs transition-colors duration-200 ${
                      isSubActive
                        ? 'bg-blue-900 text-blue-400 font-medium'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${isSubActive ? 'text-blue-400' : 'text-gray-500'}`} />
                    <span>{subItem.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMobileMenuItem = (item) => {
    const isOpen = openDropdown === item.name;
    const hasActiveItem = item.items?.some(subItem => isActive(subItem.path)) || isActive(item.path);

    if (item.single) {
      return (
        <Link
          key={item.name}
          to={item.path}
          className={`flex items-center space-x-3 px-4 py-2.5 border-b border-gray-700 text-sm ${
            isActive(item.path)
              ? 'bg-blue-900 text-blue-400 font-medium'
              : 'text-gray-300'
          }`}
        >
          <item.icon className="h-4 w-4" />
          <span>{item.label}</span>
        </Link>
      );
    }

    return (
      <div key={item.name} className="border-b border-gray-700 last:border-b-0">
        <button
          type="button"
          onClick={(e) => toggleDropdown(e, item.name)}
          className={`w-full flex items-center justify-between px-4 py-2.5 text-sm ${
            hasActiveItem || isOpen
              ? 'bg-blue-900 text-blue-400 font-medium'
              : 'text-gray-300'
          }`}
        >
          <div className="flex items-center space-x-3">
            <item.icon className="h-4 w-4" />
            <span>{item.label}</span>
          </div>
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="bg-gray-900 px-4 py-1.5 space-y-0.5">
            {item.items.map((subItem) => {
              const Icon = subItem.icon;
              const isSubActive = isActive(subItem.path);

              return (
                <Link
                  key={subItem.path}
                  to={subItem.path}
                  className={`flex items-center space-x-2.5 px-3 py-2 rounded text-xs ${
                    isSubActive
                      ? 'bg-blue-900 text-blue-400 font-medium'
                      : 'text-gray-400 hover:bg-gray-800'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{subItem.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Top Navigation Bar - Always Dark (WiseCP Style) */}
      <nav className="bg-gray-900 border-b border-gray-800 shadow-sm sticky top-0 z-40">
        {/* Top Bar - Logo, Search, User Controls */}
        <div className="px-3 lg:px-4">
          <div className="flex items-center justify-between h-11">
            {/* Left - Logo */}
            <Link to="/dashboard" className="flex items-center space-x-2 min-h-[36px]">
              {appLogoUrl && (
                <div className="h-9 rounded flex items-center justify-center overflow-hidden">
                  <img
                    src={appLogoUrl}
                    alt="App Logo"
                    className="h-full object-contain"
                  />
                </div>
              )}
            </Link>

            {/* Center - Search Bar (Desktop) */}
            <div className="hidden md:flex flex-1 max-w-sm mx-6">
              <div className="relative w-full" ref={searchRef}>
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                {searchLoading && (
                  <Loader2 className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-blue-500 animate-spin" />
                )}
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('header.search', 'Smart Search...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => searchResults && setShowSearchResults(true)}
                  className="w-full pl-8 pr-9 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                
                {/* Search Results Dropdown */}
                {showSearchResults && searchResults?.results && searchResults.results.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg max-h-96 overflow-y-auto z-50">
                    {searchResults.results.map((category, catIndex) => (
                      category.items.length > 0 && (
                        <div key={catIndex} className="border-b border-gray-700 last:border-b-0">
                          <div className="px-3 py-1.5 bg-gray-750 text-xs font-semibold text-gray-400 sticky top-0">
                            {category.category} ({category.items.length})
                          </div>
                          {category.items.map((item, itemIndex) => {
                            const globalIndex = searchResults.results.slice(0, catIndex).reduce((sum, cat) => sum + cat.items.length, 0) + itemIndex;
                            const isSelected = globalIndex === selectedResultIndex;
                            const Icon = category.icon || ExternalLink;
                            
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleResultClick(item.link)}
                                className={`w-full flex items-start space-x-2.5 px-3 py-2 text-left transition-colors ${
                                  isSelected ? 'bg-blue-900/50' : 'hover:bg-gray-700'
                                }`}
                              >
                                <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${isSelected ? 'text-blue-400' : 'text-gray-500'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className={`text-xs font-medium truncate ${isSelected ? 'text-blue-400' : 'text-white'}`}>
                                    {item.title}
                                  </div>
                                  {item.subtitle && (
                                    <div className="text-xs text-gray-400 truncate mt-0.5">
                                      {item.subtitle}
                                    </div>
                                  )}
                                  {item.meta && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {item.meta}
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )
                    ))}
                  </div>
                )}
                
                {showSearchResults && searchResults?.results && searchResults.results.every(cat => cat.items.length === 0) && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50">
                    <div className="px-3 py-6 text-center text-xs text-gray-400">
                      Sonuç bulunamadı
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right - User Controls */}
            <div className="flex items-center space-x-0.5">
              {/* Notifications */}
              <button type="button" className="relative p-1.5 rounded text-gray-400 hover:bg-gray-800 transition-colors">
                <Bell className="h-4 w-4" />
                <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 bg-red-500 rounded-full"></span>
              </button>

              {/* Language Selector */}
              <div className="relative" ref={languageMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className="flex items-center space-x-1 px-2 py-1.5 rounded text-gray-400 hover:bg-gray-800 transition-colors"
                >
                  <span className="text-sm">{currentLanguage?.flag || '🌐'}</span>
                  <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${showLanguageMenu ? 'rotate-180' : ''}`} />
                </button>

                {showLanguageMenu && (
                  <div className="absolute right-0 mt-1 w-40 bg-gray-800 border border-gray-700 rounded shadow-lg z-50">
                    <div className="py-1">
                      {AVAILABLE_LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          type="button"
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full flex items-center space-x-2.5 px-3 py-1.5 text-xs transition-colors duration-200 ${
                            i18n.language === lang.code
                              ? 'bg-blue-900 text-blue-400 font-medium'
                              : 'text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          <span className="text-base">{lang.flag || '🌐'}</span>
                          <span>{lang.nativeName}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Dark Mode Toggle */}
              <button
                type="button"
                onClick={toggleTheme}
                className="p-1.5 rounded text-gray-400 hover:bg-gray-800 transition-colors"
                title={theme === 'dark' ? t('header.lightMode', 'Açık Mod') : t('header.darkMode', 'Koyu Mod')}
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </button>

              {/* User Menu */}
              {user && (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-1.5 px-2 py-1.5 rounded hover:bg-gray-800 transition-colors"
                  >
                    <div className="h-6 w-6 bg-blue-900 rounded-full flex items-center justify-center">
                      <User className="h-3 w-3 text-blue-400" />
                    </div>
                    <ChevronDown className={`h-3 w-3 text-gray-500 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-1 w-44 bg-gray-800 border border-gray-700 rounded shadow-lg z-50">
                      <div className="py-1">
                        <div className="px-3 py-2 border-b border-gray-700">
                          <p className="text-xs font-medium text-white">
                            {user.fullName || user.username || t('header.user', 'Kullanıcı')}
                          </p>
                          <p className="text-xs text-gray-400 capitalize">
                            {user.role || t('header.user', 'Kullanıcı')}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="w-full flex items-center space-x-2 px-3 py-2 text-xs text-red-400 hover:bg-red-900/20 transition-colors"
                        >
                          <LogOut className="h-3.5 w-3.5" />
                          <span>{t('header.logout', 'Çıkış Yap')}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-1.5 rounded text-gray-400 hover:bg-gray-800 transition-colors"
              >
                {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar - Navigation Menu */}
        <div className="border-t border-gray-800 bg-gray-900 overflow-visible">
          <div className="px-3 lg:px-4 overflow-visible">
            {/* Desktop Navigation Menu */}
            <div className="hidden lg:flex items-center space-x-0.5 py-1.5 overflow-visible">
              {menuItems.map((item) => 
                item.single ? renderSingleItem(item) : renderDropdown(item)
              )}
            </div>

            {/* Mobile Search */}
            <div className="md:hidden py-1.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                {searchLoading && (
                  <Loader2 className="absolute right-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-blue-500 animate-spin" />
                )}
                <input
                  type="text"
                  placeholder={t('header.search', 'Smart Search...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => searchResults && setShowSearchResults(true)}
                  className="w-full pl-8 pr-9 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                
                {/* Mobile Search Results */}
                {showSearchResults && searchResults?.results && searchResults.results.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg max-h-80 overflow-y-auto z-50">
                    {searchResults.results.map((category, catIndex) => (
                      category.items.length > 0 && (
                        <div key={catIndex} className="border-b border-gray-700 last:border-b-0">
                          <div className="px-3 py-1.5 bg-gray-900 text-xs font-semibold text-gray-400">
                            {category.category} ({category.items.length})
                          </div>
                          {category.items.map((item, itemIndex) => {
                            const globalIndex = searchResults.results.slice(0, catIndex).reduce((sum, cat) => sum + cat.items.length, 0) + itemIndex;
                            const isSelected = globalIndex === selectedResultIndex;
                            
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => handleResultClick(item.link)}
                                className={`w-full flex items-start space-x-2.5 px-3 py-2 text-left transition-colors ${
                                  isSelected ? 'bg-blue-900/50' : 'hover:bg-gray-700'
                                }`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className={`text-xs font-medium truncate ${isSelected ? 'text-blue-400' : 'text-white'}`}>
                                    {item.title}
                                  </div>
                                  {item.subtitle && (
                                    <div className="text-xs text-gray-400 truncate mt-0.5">
                                      {item.subtitle}
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-800 bg-gray-900">
            <div className="max-h-[60vh] overflow-y-auto">
              {menuItems.map((item) => renderMobileMenuItem(item))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default TopNavigation;
